import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";

import { ApiURLs } from "../../constant/ApiUrls";
import { fetchData, postData } from "../../services/api";
import { cur } from "../../constant/Constants";

/**
 * Advert pricing: what each tier costs in each category.
 *
 * The site used to charge one price per tier everywhere - Lite was 4 euro
 * whether you were selling a car or a jumper - and there was nowhere to change
 * that without a code change. This is that screen.
 *
 * The rule the whole page is built around: a price flows downhill. Put a number
 * on Motors and every category under it charges that. Put a different number on
 * Cars For Sale and that one category overrides while the rest of Motors carries
 * on at the section price. Leave a box empty and it inherits.
 *
 * Which is why an empty box is not the same as zero, and the page has to say so
 * out loud. An empty box shows the inherited number in grey; a box with 0 in it
 * means free, and free is a decision somebody made. Storing "empty" as 0 would
 * quietly make a tier free everywhere it was left blank, so a blank cell is
 * never sent as a number - it is sent as a deletion.
 */

const money = (v) => `${cur}${Number(v || 0).toFixed(2)}`;

// "" and null both mean "inherit". 0 does not.
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

export default function AdPricing() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [categories, setCategories] = useState([]);

  // category id -> plan id -> the text in the box. Only what has been typed or
  // loaded; a missing entry is an empty box.
  const [cells, setCells] = useState({});
  const [defaults, setDefaults] = useState({});

  // What was loaded, so Save can send only what actually changed rather than
  // rewriting every cell on the screen.
  const [loadedCells, setLoadedCells] = useState({});
  const [loadedDefaults, setLoadedDefaults] = useState({});

  const [openSections, setOpenSections] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData(ApiURLs.PLAN_PRICING);
      const d = (res && res.data) || {};
      const p = d.plans || [];
      setPlans(p);
      setCategories(d.categories || []);

      const c = {};
      for (const row of d.prices || []) {
        c[row.category_id] = c[row.category_id] || {};
        c[row.category_id][row.plan_id] = String(row.price);
      }
      setCells(c);
      setLoadedCells(JSON.parse(JSON.stringify(c)));

      const dd = {};
      for (const plan of p) dd[plan.id] = String(plan.price);
      setDefaults(dd);
      setLoadedDefaults({ ...dd });
    } catch (e) {
      toast.error("Could not load the pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* The category tree, sections first. Only two levels deep today, but the
     children are found by parent id rather than by position, so a third level
     added from Categories Management still lands under the right section. */
  const sections = useMemo(() => {
    const roots = categories.filter((c) => !c.parent_id);
    const kids = new Map();
    for (const c of categories) {
      if (!c.parent_id) continue;
      const k = Number(c.parent_id);
      if (!kids.has(k)) kids.set(k, []);
      kids.get(k).push(c);
    }
    return roots
      .map((r) => ({ ...r, children: (kids.get(Number(r.id)) || []).slice().sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categories]);

  const parentOf = useMemo(() => {
    const m = new Map();
    for (const c of categories) m.set(Number(c.id), c.parent_id === null ? null : Number(c.parent_id));
    return m;
  }, [categories]);

  /* What this cell would charge if it were left empty: the nearest ancestor
     with a price, and failing that the tier's own price. Exactly the walk the
     server does, so the grey number on screen is the number a seller pays. */
  const inherited = useCallback((categoryId, planId) => {
    let cur2 = parentOf.get(Number(categoryId));
    let guard = 0;
    while (cur2 && guard++ < 12) {
      const v = cells[cur2] && cells[cur2][planId];
      if (!isBlank(v)) return Number(v);
      cur2 = parentOf.get(cur2);
    }
    const d = defaults[planId];
    return isBlank(d) ? 0 : Number(d);
  }, [cells, defaults, parentOf]);

  const setCell = (categoryId, planId, value) => {
    setCells((prev) => {
      const next = { ...prev, [categoryId]: { ...(prev[categoryId] || {}) } };
      if (isBlank(value)) delete next[categoryId][planId];
      else next[categoryId][planId] = value;
      return next;
    });
  };

  const changes = useMemo(() => {
    const out = { cells: [], defaults: [] };
    const seen = new Set();
    const ids = new Set([...Object.keys(cells), ...Object.keys(loadedCells)]);
    for (const catId of ids) {
      const now = cells[catId] || {};
      const was = loadedCells[catId] || {};
      const planIds = new Set([...Object.keys(now), ...Object.keys(was)]);
      for (const planId of planIds) {
        const key = `${catId}:${planId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const a = isBlank(now[planId]) ? null : String(now[planId]);
        const b = isBlank(was[planId]) ? null : String(was[planId]);
        if (a === b) continue;
        if (a !== null && b !== null && Number(a) === Number(b)) continue;
        out.cells.push({ plan_id: Number(planId), category_id: Number(catId), price: a });
      }
    }
    for (const planId of Object.keys(defaults)) {
      const a = defaults[planId];
      const b = loadedDefaults[planId];
      if (isBlank(a)) continue;
      if (!isBlank(b) && Number(a) === Number(b)) continue;
      out.defaults.push({ plan_id: Number(planId), price: Number(a) });
    }
    return out;
  }, [cells, loadedCells, defaults, loadedDefaults]);

  const dirty = changes.cells.length + changes.defaults.length;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const res = await postData(changes, ApiURLs.PLAN_PRICING, false);
      if (res && res.status === 1) {
        toast.success(`Pricing saved (${dirty} change${dirty === 1 ? "" : "s"})`);
        await load();
      } else {
        toast.error((res && res.message) || "Could not save the pricing");
      }
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.message) || "Could not save the pricing");
    } finally {
      setSaving(false);
    }
  };

  const countOverrides = (section) => {
    let n = 0;
    const check = (id) => {
      const row = cells[id];
      if (row) n += Object.keys(row).length;
    };
    check(section.id);
    for (const c of section.children) check(c.id);
    return n;
  };

  const PriceBox = ({ categoryId, plan }) => {
    const value = (cells[categoryId] && cells[categoryId][plan.id]) ?? "";
    const ghost = inherited(categoryId, plan.id);
    return (
      <TextField
        size="small"
        value={value}
        placeholder={Number(ghost).toFixed(2)}
        onChange={(e) => setCell(categoryId, plan.id, e.target.value)}
        inputProps={{ inputMode: "decimal", style: { textAlign: "right", width: 62 } }}
        InputProps={{ startAdornment: <InputAdornment position="start">{cur}</InputAdornment> }}
        sx={{ "& input::placeholder": { color: "#9aa5ad", opacity: 1 } }}
      />
    );
  };

  const Row = ({ category, indent }) => (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 2,
        padding: "6px 8px", paddingLeft: `${8 + indent * 22}px`,
        borderTop: "1px solid #eef1f3",
      }}
    >
      <Typography sx={{ flex: 1, fontSize: 14, fontWeight: indent ? 400 : 600 }}>
        {category.name}
      </Typography>
      {plans.map((plan) => (
        <PriceBox key={plan.id} categoryId={category.id} plan={plan} />
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Advert Pricing</Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        A price flows downhill. Put a price on a section and every category under it
        charges that. Put a different price on one category and it overrides just that
        one. Leave a box empty and it inherits - the grey number in an empty box is what
        that category actually charges today. Type 0 to make a tier free somewhere.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          Default price for each tier
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#68757d", mb: 1.5 }}>
          What a category charges when nothing has been set for it or the section above it.
        </Typography>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {plans.map((plan) => (
            <Box key={plan.id} sx={{ minWidth: 190 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {plan.planName}{" "}
                {plan.recommended ? <Chip size="small" label="Recommended" sx={{ ml: 0.5 }} /> : null}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#68757d", mb: 0.5 }}>
                {plan.daysOfListing} days · {plan.numberOfPhotos} photos · {plan.bump} bumps
                {plan.priorityPlacement ? " · priority" : ""}
              </Typography>
              <TextField
                size="small"
                value={defaults[plan.id] ?? ""}
                onChange={(e) => setDefaults((p) => ({ ...p, [plan.id]: e.target.value }))}
                inputProps={{ inputMode: "decimal", style: { textAlign: "right", width: 80 } }}
                InputProps={{ startAdornment: <InputAdornment position="start">{cur}</InputAdornment> }}
              />
            </Box>
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 0, mb: 2, overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.5, background: "#f6f8f9" }}>
          <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 14 }}>Category</Typography>
          {plans.map((plan) => (
            <Typography key={plan.id} sx={{ fontWeight: 700, fontSize: 13, width: 118, textAlign: "center" }}>
              {plan.planName}
            </Typography>
          ))}
        </Box>

        {sections.map((section) => {
          const open = !!openSections[section.id];
          const overrides = countOverrides(section);
          return (
            <Box key={section.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: "6px 8px", borderTop: "1px solid #e3e8ea", background: "#fbfcfc" }}>
                <Button
                  size="small"
                  onClick={() => setOpenSections((p) => ({ ...p, [section.id]: !open }))}
                  sx={{ minWidth: 30, p: 0, mr: 0.5 }}
                >
                  {open ? "−" : "+"}
                </Button>
                <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700 }}>
                  {section.name}
                  <Typography component="span" sx={{ fontSize: 12, color: "#68757d", ml: 1 }}>
                    {section.children.length} categor{section.children.length === 1 ? "y" : "ies"}
                    {overrides ? ` · ${overrides} price${overrides === 1 ? "" : "s"} set` : ""}
                  </Typography>
                </Typography>
                {plans.map((plan) => (
                  <PriceBox key={plan.id} categoryId={section.id} plan={plan} />
                ))}
              </Box>
              {open && section.children.map((child) => (
                <Row key={child.id} category={child} indent={1} />
              ))}
            </Box>
          );
        })}
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, position: "sticky", bottom: 0, background: "#fff", p: 1.5, borderTop: "1px solid #e3e8ea" }}>
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? "Saving…" : dirty ? `Save ${dirty} change${dirty === 1 ? "" : "s"}` : "Nothing to save"}
        </Button>
        <Button variant="outlined" onClick={load} disabled={saving}>Discard changes</Button>
        <Typography sx={{ fontSize: 13, color: "#68757d" }}>
          Tiers today: {plans.map((p) => `${p.planName} ${money(p.price)}`).join("  ·  ")}
        </Typography>
      </Box>
    </Box>
  );
}
